import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BRAND } from "@/lib/site";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { delay: 0.15 + i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      id="home"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-gradient-to-b from-zi-tint via-zi-tint2 to-white pt-28 pb-16"
    >
      <div className="pointer-events-none absolute -left-40 top-10 h-[520px] w-[520px] rounded-full bg-zi-accent/25 blur-[130px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[440px] w-[440px] rounded-full bg-zi-primary/15 blur-[130px]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          <h1 className="font-heading text-[2.6rem] font-bold leading-[1.03] tracking-tight text-zi-ink sm:text-6xl lg:text-7xl">
            <MaskLine i={0}>Quality BPO</MaskLine>
            <MaskLine i={1}>Solutions For</MaskLine>
            <MaskLine i={2}>
              <span className="bg-gradient-to-r from-zi-primary to-zi-accent bg-clip-text text-transparent">Businesses.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-6 max-w-xl font-sans text-base leading-relaxed text-zi-muted sm:text-lg"
          >
            Team Zippy is right here to grow your business revenue with our
            fantastic services — dependable calling, lead generation, admin and
            back-office support, all under one roof.
          </motion.p>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-3xl border border-zi-line bg-white shadow-2xl shadow-zi-primary/10"
          >
            <motion.img
              src={BRAND.heroImage}
              alt="Business support team at work"
              loading="eager"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zi-ink/70 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/40 bg-white/90 p-4 backdrop-blur-md">
              <div className="font-heading text-sm font-bold text-zi-ink">Grow revenue, reduce operational costs</div>
              <div className="font-sans text-xs text-zi-muted">Your trusted BPO partner.</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
